/**
 * Client-side metering API for credit balance queries.
 * @module lib/metering
 */

import { captureException } from '@/lib/telemetry/posthog/frontend';

/** Credit balance response from the metering API */
export interface CreditBalance {
  /** Number of credits available for use */
  available_credits: number;
  /** Number of credits currently reserved by active sessions */
  reserved_credits: number;
  /** Total credits (available + reserved) */
  total_credits: number;
}

/**
 * Fetches the current credit balance for the authenticated user or organization.
 * @param authToken - Supabase JWT token
 * @param orgId - Optional organization ID. When provided, credits are scoped to the org pool.
 * @returns Credit balance or null on failure
 */
export async function fetchCredits(authToken: string, orgId?: string): Promise<CreditBalance | null> {
  try {
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${authToken}`,
    };
    if (orgId) {
      headers['X-Organization-ID'] = orgId;
    }

    const response = await fetch('/api/metering/credits', { headers });
    if (!response.ok) {
      // Don't capture here - let the calling hook/component capture once with full context.
      // The server side (Pages Function + GCP Proxy) captures the rich upstream details.
      return null;
    }
    return await response.json() as CreditBalance;
  } catch (error) {
    captureException(error instanceof Error ? error : String(error), 'metering.fetchCredits', { orgId });
    return null;
  }
}
